import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  api, withQuery, money, date, DOC_TYPE_LABEL, STATUS_LABEL, STATUS_CLASS, isValidEik, isoDate,
} from '../api.js';
import Lookup from '../components/Lookup.jsx';
import ProductLookup from '../components/ProductLookup.jsx';
import DateField from '../components/DateField.jsx';
import GroupPicker from '../components/GroupPicker.jsx';
import { useAuth } from '../App.jsx';
import { usePaymentMethods } from '../hooks.js';

const TYPE_OPTIONS = [
  ['client', 'Клиент'],
  ['supplier', 'Доставчик'],
  ['both', 'Клиент и доставчик'],
  ['agent', 'Агент'],
  ['other', 'Друг'],
];

const PRICE_LEVEL_OPTIONS = [
  ['retail', 'Дребно'],
  ['wholesale', 'Едро'],
];

/** Полетата на формата -> имената, които API-то приема. */
const emptyForm = {
  name: '', type: 'client', isPerson: false,
  eik: '', vatNumber: '', isVatRegistered: false,
  country: 'BG', city: '', address: '', postCode: '',
  shipCity: '', shipAddress: '',
  phone: '', email: '', mol: '',
  bankName: '', bankBic: '', bankIban: '',
  defaultPriceLevel: 'retail', defaultPaymentMethod: 'cash', discountPercent: 0,
  creditLimit: 0, creditDays: 14, blockOnOverdue: false, openingBalance: 0,
  agentId: null, agentName: null, agentSince: null, commissionPercent: 0,
  isActive: true, note: '',
};

export default function PartnerView({ mode = 'view' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = mode === 'new';

  const [partner, setPartner] = useState(null);
  const [form, setForm] = useState(isNew ? emptyForm : null);
  const [editing, setEditing] = useState(isNew);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lookup, setLookup] = useState(null);   // { state, message, partner }
  const { methods: paymentMethods } = usePaymentMethods();

  const canWrite = user.role !== 'auditor';
  const canEditBalance = user.role === 'admin';

  // Само доставчик — не и „клиент и доставчик“, при който продажби
  // има и полетата за тях остават нужни.
  const onlySupplier = form?.type === "supplier";

  // Агент се посочва само при клиент: доставчик не носи комисионна, а
  // агентът не е клиент на друг агент.
  const isClientLike = form?.type === 'client' || form?.type === 'both';

  useEffect(() => {
    if (isNew) return;
    setPartner(null);
    api.get(`/api/partners/${id}`)
      .then((p) => { setPartner(p); setForm(toForm(p)); })
      .catch((e) => setError(e.message));
  }, [id, isNew]);

  function set(patch) { setForm((f) => ({ ...f, ...patch })); }

  /**
   * Търси по ЕИК: първо в нашата база, после в Търговския регистър.
   * Намерените данни само попълват празните полета — вече въведеното
   * от човек не се презаписва.
   */
  async function searchByEik() {
    const eik = (form.eik ?? '').trim();
    if (!eik) return;

    // Проверено веднага, без да се чака сървъра — печатна грешка в ЕИК-а
    // няма как да намери фирма, а обикаля през мрежата за нищо.
    if (!isValidEik(eik)) {
      setLookup({
        state: 'error',
        message: 'ЕИК-ът не е валиден — трябва да е 9 или 13 цифри с вярна контролна цифра',
      });
      return;
    }

    setLookup({ state: 'searching' });
    try {
      const res = await api.get(withQuery(`/api/lookup/company/${encodeURIComponent(eik)}`,
        { exceptId: isNew ? undefined : id }));

      if (res.duplicate) {
        setLookup({ state: 'duplicate', partner: res.partner });
        return;
      }

      const c = res.company;
      /*
       * Съществуваща фирма — „Опресни“ (23.10.2026): данните от регистъра
       * заместват старите (сменено име, адрес, МОЛ…). Телефон и поща остават
       * наши, ако регистърът няма. Нищо не се записва без „Запази“.
       */
      if (!isNew) {
        setForm((f) => {
          const n = { ...f };
          for (const [k, v] of Object.entries({
            name: c.name, city: c.city, address: c.address, postCode: c.postCode,
            mol: c.mol, vatNumber: c.vatNumber, phone: c.phone, email: c.email, country: c.country,
          })) {
            if (v && String(v).trim() && String(v).trim() !== String(f[k] ?? '').trim()) { n[k] = v; }
          }
          if (c.isVatRegistered && !f.isVatRegistered) { n.isVatRegistered = true; }
          return n;
        });
        setLookup({
          state: 'found',
          message: `Опреснено от: ${c.sources.join(' и ')}. Прегледайте и натиснете „Запази“ (или „Откажи“).`,
        });
        return;
      }
      setForm((f) => ({
        ...f,
        name: f.name.trim() || c.name || '',
        city: f.city || c.city || '',
        address: f.address || c.address || '',
        postCode: f.postCode || c.postCode || '',
        country: c.country || f.country,
        phone: f.phone || c.phone || '',
        email: f.email || c.email || '',
        mol: f.mol || c.mol || '',
        vatNumber: f.vatNumber || c.vatNumber || '',
        isVatRegistered: c.isVatRegistered || f.isVatRegistered,
      }));
      setLookup({ state: 'found', message: `Данните са от: ${c.sources.join(' и ')}` });
    } catch (err) {
      setLookup({ state: 'error', message: err.message });
    }
  }

  /**
   * Спиране и пускане с един клик.
   *
   * Спреният не се вижда в списъците и в менютата за избор, но всичко
   * зад него си остава — затова това, а не изтриването, е обичайният
   * начин да се разчисти. Връзките му с клиенти се пазят: пуснат
   * обратно, той е какъвто си е бил.
   */
  async function toggleActive() {
    const next = !form.isActive;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.patch(`/api/partners/${id}`, { isActive: next });
      setPartner((p) => ({ ...p, ...updated }));
      setForm((f) => ({ ...f, isActive: !!Number(updated.is_active) }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Изтриване на агент.
   *
   * Клиентите му остават без агент — затова се казва колко са, преди да
   * се потвърди. Агент, минал по документи, изобщо не стига дотук:
   * сървърът го отказва и подсказва да бъде спрян.
   */
  async function removeAgent() {
    const linked = await api.get(`/api/partners/${id}/clients`).catch(() => ({ items: [] }));
    const n = linked.items?.length ?? 0;
    const warn = n > 0
      ? `\n\n${n} ${n === 1 ? 'клиент остава' : 'клиента остават'} без агент.`
      : '';
    if (!window.confirm(`Изтриване на агент „${form.name}"?${warn}`)) return;

    setBusy(true);
    setError(null);
    try {
      await api.del(`/api/partners/${id}`);
      navigate('/partners', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const payload = { ...form };
      if (!canEditBalance) delete payload.openingBalance;
      // Празните текстови полета отиват като null, за да не пълним базата с "".
      for (const [k, v] of Object.entries(payload)) {
        if (typeof v === 'string' && v.trim() === '') payload[k] = null;
      }
      payload.name = form.name.trim();
      payload.discountPercent = Number(form.discountPercent) || 0;
      payload.creditLimit = Number(form.creditLimit) || 0;
      payload.creditDays = Number(form.creditDays) || 0;
      payload.blockOnOverdue = !!form.blockOnOverdue;
      if (canEditBalance) payload.openingBalance = Number(form.openingBalance) || 0;
      // Името на агента се показва, но се пази при него самия.
      delete payload.agentName;
      payload.agentId = form.agentId ? Number(form.agentId) : null;
      payload.agentSince = payload.agentId ? (form.agentSince || null) : null;
      payload.commissionPercent = Number(form.commissionPercent) || 0;

      if (isNew) {
        const created = await api.post('/api/partners', payload);
        navigate(`/partners/${created.id}`, { replace: true });
      } else {
        const updated = await api.patch(`/api/partners/${id}`, payload);
        setPartner((p) => ({ ...updated, recentDocuments: p.recentDocuments, turnover: p.turnover, due_eur: p.due_eur, owed_eur: p.owed_eur }));
        setForm(toForm(updated));
        setEditing(false);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (error && !form) return <div className="error">{error}</div>;
  if (!form) return <div className="empty">Зареждане…</div>;

  return (
    <>
      <div className="page-head">
        <h1>{isNew ? 'Нов контрагент' : partner?.name}</h1>
        {!isNew && partner && !partner.is_active && <span className="badge muted">неактивен</span>}
        {!isNew && partner && Number(partner.due_eur) > 0.01 && (
          <span className="badge danger">дължи {money(partner.due_eur)}</span>
        )}
        <div className="spacer" />
        {/* Триенето стои настрана от „Редактирай" — рядко се ползва и
            не бива да се цъка по погрешка вместо съседа си. */}
        {/* Спирането е един клик, защото е обичайният изход: агент, минал
            по документи, не се трие, а се спира и изчезва от списъците. */}
        {!editing && !isNew && canWrite && form.type === 'agent' && (
          <button onClick={toggleActive} disabled={busy}>
            {form.isActive ? 'Спри' : 'Пусни'}
          </button>
        )}
        {!editing && !isNew && canWrite && form.type === 'agent' && (
          <button className="danger" onClick={removeAgent} disabled={busy}>Изтрий</button>
        )}
        {!editing && canWrite && (
          <button className="primary" onClick={() => setEditing(true)}>Редактирай</button>
        )}
        {editing && !isNew && (
          <button onClick={() => { setForm(toForm(partner)); setEditing(false); setError(null); }}>
            Откажи
          </button>
        )}
        {editing && (
          <button className="primary" onClick={save} disabled={busy || form.name.trim().length < 2}>
            {busy ? 'Запазване…' : 'Запази'}
          </button>
        )}
        <Link to="/partners">← към списъка</Link>
      </div>

      {error && <div className="error">{error}</div>}

      {lookup?.state === 'duplicate' && (
        <div className="error">
          <strong>Дублиран ЕИК.</strong> Този контрагент вече съществува:{' '}
          <Link to={`/partners/${lookup.partner.id}`}>{lookup.partner.name}</Link>
          {lookup.partner.city ? ` — ${lookup.partner.city}` : ''}
          {!Number(lookup.partner.is_active) && ' (неактивен)'}
        </div>
      )}
      {partner?.credit?.hasOverdue && (
        <div className={partner.credit.blocked ? 'error' : 'warning'}>
          <strong>
            {partner.credit.blocked ? 'Продажбите са спрени. ' : 'Просрочено задължение. '}
          </strong>
          {money(partner.credit.overdueAmount)} при срок {partner.credit.creditDays} дни
          {partner.credit.usesDefaultDays ? ' (по подразбиране)' : ''}.
          {partner.credit.documents.length > 0 && (
            <> Най-старото: {partner.credit.documents[0].docNumber} от{' '}
            {date(partner.credit.documents[0].docDate)}, просрочено с{' '}
            {partner.credit.documents[0].daysLate} дни.</>
          )}
          {partner.credit.openingOverdue && ' Включва началното салдо от миграцията.'}
          {!partner.credit.blocked && ' Спирането не е включено за този контрагент.'}
        </div>
      )}
      {lookup?.state === 'found' && <div className="notice">{lookup.message}</div>}
      {lookup?.state === 'error' && <div className="warning">{lookup.message}</div>}

      <div className="card">
        <div className="card-head">Основни данни</div>
        <div className="card-body">
          <div className="form-grid">
            <Field label="Наименование" wide>
              <input value={form.name} onChange={(e) => set({ name: e.target.value })}
                disabled={!editing} autoComplete="off" />
            </Field>
            <Field label="Вид">
              <select value={form.type} onChange={(e) => set({ type: e.target.value })} disabled={!editing}>
                {TYPE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="ЕИК / Булстат">
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  value={form.eik}
                  onChange={(e) => { set({ eik: e.target.value }); setLookup(null); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); searchByEik(); }
                  }}
                  disabled={!editing}
                  inputMode="numeric"
                  autoComplete="off"
                />
                {editing && (
                  <button
                    type="button"
                    onClick={searchByEik}
                    disabled={!form.eik?.trim() || lookup?.state === 'searching'}
                    title={isNew ? 'Търси в нашата база и в Търговския регистър' : 'Опресни данните от Търговския регистър (сменено име, адрес, МОЛ…)'}
                  >
                    {lookup?.state === 'searching' ? '…' : (isNew ? 'Търси' : 'Опресни')}
                  </button>
                )}
              </div>
            </Field>
            <Field label="ДДС номер">
              {/* Chrome иначе слага тук ЕИК-а на собствената ни фирма. */}
              <input value={form.vatNumber} onChange={(e) => set({ vatNumber: e.target.value })}
                disabled={!editing} autoComplete="off" name="vat-no-autofill" />
            </Field>
            <Check label="Регистриран по ДДС" checked={form.isVatRegistered} disabled={!editing}
              onChange={(v) => set({ isVatRegistered: v })} />
            <Check label="Физическо лице" checked={form.isPerson} disabled={!editing}
              onChange={(v) => set({ isPerson: v })} />
            <Check label="Активен" checked={form.isActive} disabled={!editing}
              onChange={(v) => set({ isActive: v })} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">Адрес и контакти</div>
        <div className="card-body">
          <div className="form-grid">
            <Field label="Град">
              <input value={form.city} onChange={(e) => set({ city: e.target.value })} disabled={!editing} />
            </Field>
            <Field label="Адрес" wide>
              <input value={form.address} onChange={(e) => set({ address: e.target.value })} disabled={!editing} />
            </Field>
            <Field label="Пощенски код">
              <input value={form.postCode} onChange={(e) => set({ postCode: e.target.value })} disabled={!editing} />
            </Field>
            <Field label="Държава">
              <input value={form.country} maxLength={2} onChange={(e) => set({ country: e.target.value.toUpperCase() })} disabled={!editing} />
            </Field>
            <Field label="Телефон">
              <input value={form.phone} onChange={(e) => set({ phone: e.target.value })} disabled={!editing} />
            </Field>
            <Field label="Ел. поща">
              <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} disabled={!editing} />
            </Field>
            <Field label="МОЛ">
              <input value={form.mol} onChange={(e) => set({ mol: e.target.value })} disabled={!editing} />
            </Field>
            {/* Доставката е накъде пращаме ние — при доставчик няма такова. */}
            {!onlySupplier && (
              <>
                <Field label="Град за доставка">
                  <input value={form.shipCity} onChange={(e) => set({ shipCity: e.target.value })} disabled={!editing} />
                </Field>
                <Field label="Адрес за доставка" wide>
                  <input value={form.shipAddress} onChange={(e) => set({ shipAddress: e.target.value })} disabled={!editing} />
                </Field>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">Търговски условия</div>
        <div className="card-body">
          <div className="form-grid">
            <Field label="Ценово ниво" hint="предлага се при продажба">
              <select value={form.defaultPriceLevel} onChange={(e) => set({ defaultPriceLevel: e.target.value })} disabled={!editing}>
                {PRICE_LEVEL_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Начин на плащане" hint="поема се от продажбата и доставката">
              <select value={form.defaultPaymentMethod} onChange={(e) => set({ defaultPaymentMethod: e.target.value })} disabled={!editing}>
                {/* „Подарък" е за отделен ред от продажба, не начин, по който
                    контрагент по подразбиране плаща — API-то не го приема тук. */}
                {paymentMethods.filter((m) => m.code !== 'gift')
                  .map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
              </select>
            </Field>
            <Field label="Отстъпка, %">
              <input type="number" step="0.01" min="0" max="100" value={form.discountPercent}
                onChange={(e) => set({ discountPercent: e.target.value })} disabled={!editing} />
            </Field>
            <Field label="Кредитен лимит, €">
              <input type="number" step="0.01" min="0" value={form.creditLimit}
                onChange={(e) => set({ creditLimit: e.target.value })} disabled={!editing} />
            </Field>
            <Field
              label="Отсрочка, дни"
              hint={Number(form.creditDays) > 0 ? undefined : 'нула значи плащане веднага'}
            >
              <input type="number" min="0" max="365" value={form.creditDays}
                onChange={(e) => set({ creditDays: e.target.value })} disabled={!editing} />
            </Field>
            {/* Агентът взима процент от продажбите към този клиент.
                Записва се по документа сам при продажба, затова тук е
                единственото място, на което се посочва. */}
            {isClientLike && (
              <Field label="Агент" hint="взима комисионна от продажбите към този клиент">
                {form.agentId ? (
                  <div className="field-static" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Link to={`/partners/${form.agentId}`}>{form.agentName ?? 'агент'}</Link>
                    {editing ? (
                      <>
                        <span className="muted" style={{ fontSize: 12 }}>от</span>
                        <DateField value={form.agentSince ?? ''} onChange={(v) => set({ agentSince: v || null })} />
                        <button className="link" onClick={() => set({ agentId: null, agentName: null, agentSince: null })}>махни</button>
                      </>
                    ) : (
                      <span className="muted" style={{ fontSize: 12 }}>
                        {form.agentSince ? `от ${date(form.agentSince)}` : 'от край време'}
                      </span>
                    )}
                  </div>
                ) : editing ? (
                  <Lookup
                    url="/api/partners" params={{ type: 'agent' }} value={null} label=""
                    placeholder="търсене на агент"
                    onPick={(agentId, agentName) => set({
                      agentId, agentName, agentSince: isoDate(),
                    })}
                  />
                ) : <span className="muted">няма</span>}
              </Field>
            )}

            {form.type === 'agent' && (
              <Field label="Комисионна, %" hint="общият процент; по групи се задава отдолу">
                <input type="number" step="0.01" min="0" max="100" value={form.commissionPercent}
                  onChange={(e) => set({ commissionPercent: e.target.value })} disabled={!editing} />
              </Field>
            )}

            {/* Спирането пази нас от неплатили клиенти; към доставчик
                няма какво да спираме. Началното салдо при доставчик пък
                се вижда като „Ние му дължим“ горе. */}
            {!onlySupplier && (
              <>
                <Check
                  label="Спирай продажби при просрочие"
                  checked={form.blockOnOverdue}
                  disabled={!editing}
                  onChange={(v) => set({ blockOnOverdue: v })}
                />
                <Field label="Начално салдо, €" hint={canEditBalance ? 'положително значи, че ни дължи' : 'само администратор'}>
                  <input type="number" step="0.01" value={form.openingBalance}
                    onChange={(e) => set({ openingBalance: e.target.value })}
                    disabled={!editing || !canEditBalance} />
                </Field>
              </>
            )}
          </div>

          <label className="field" style={{ marginTop: 14 }}>
            Забележка
            <textarea rows={2} value={form.note ?? ''} onChange={(e) => set({ note: e.target.value })} disabled={!editing} />
          </label>
        </div>
      </div>

      {!isNew && partner && (
        <>
          <PartnerFigures partner={partner} role={form.type} />

          {(form.type === 'client' || form.type === 'both') && (
            <PartnerPrices partner={partner} canWrite={canWrite} />
          )}

          {(form.type === 'supplier' || form.type === 'both') && (
            <SupplierLinks partnerId={partner.id} canWrite={canWrite} />
          )}

          {form.type === 'agent' && (
            <>
              <AgentClients agentId={partner.id} canWrite={canWrite} />
              <AgentCommissions
                agentId={partner.id}
                general={Number(partner.commission_percent ?? 0)}
                canWrite={canWrite}
              />
            </>
          )}

          <div className="card">
            <div className="card-head">Последни документи</div>
            <div className="table-wrap">
              <table className="compact">
                <thead>
                  <tr>
                    <th>Номер</th><th>Вид</th><th>Дата</th><th>Фактура</th>
                    <th className="num">Сума</th><th>Състояние</th>
                  </tr>
                </thead>
                <tbody>
                  {partner.recentDocuments?.map((d) => (
                    <tr key={d.id}>
                      <td><Link to={`/documents/${d.id}`}>{d.doc_number}</Link></td>
                      <td className="muted">{DOC_TYPE_LABEL[d.doc_type]}</td>
                      <td>{date(d.doc_date)}</td>
                      <td className="muted">{d.invoice_no ?? '—'}</td>
                      <td className="num">{money(d.total, d.currency)}</td>
                      <td><span className={`badge ${STATUS_CLASS[d.status]}`}>{STATUS_LABEL[d.status]}</span></td>
                    </tr>
                  ))}
                  {(partner.recentDocuments ?? []).length === 0 && (
                    <tr><td colSpan={6} className="empty">Няма документи</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

/**
 * Числата за контрагента.
 *
 * При доставчик интересува обратното на клиента: не колко ни дължи, а
 * колко ние му дължим, и не колко сме му продали, а колко сме взели от
 * него. При „клиент и доставчик“ се показват и двете.
 *
 * Всяка сума върви по двойка — без ДДС и с ДДС. Първото се сверява със
 * счетоводството, второто — с платеното по банка.
 */
/**
 * Запомнените връзки „каталожен номер на доставчика → наша стока".
 *
 * Заради тях второто разчитане на фактура от този доставчик минава почти
 * без ръчна работа. Но сгрешена връзка се връща при всяка следваща
 * фактура и се поправя отново и отново, без да личи откъде идва — затова
 * стои тук, на картата на доставчика, където се търси.
 */
/**
 * Цените на клиента — ценовата листа и специалните му цени.
 *
 * Листата се сменя направо, без „Редакция" на целия картон: тя е едно
 * решение, не част от данните на фирмата. Специалните цени се пазят в
 * общия екран „Ценови листи" — оттук се отваря филтриран за клиента.
 */
function PartnerPrices({ partner, canWrite }) {
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState(partner.price_list_id ?? '');
  const [rules, setRules] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get('/api/price-lists').then(setLists).catch(() => setLists([]));
    api.get(`/api/price-rules?partnerId=${partner.id}`).then(setRules).catch(() => setRules([]));
  }, [partner.id]);

  async function smeni(v) {
    setListId(v);
    setMsg(null);
    try {
      await api.put(`/api/partners/${partner.id}/price-list`, { priceListId: v ? Number(v) : null });
      setMsg('записано');
    } catch (e) { setMsg(e.message); }
  }

  const aktivni = (rules ?? []).filter((r) => r.is_active).length;

  return (
    <div className="card">
      <div className="card-head">Цени</div>
      <div className="card-body">
        <div className="toolbar" style={{ alignItems: 'flex-end' }}>
          <label className="field">
            Ценова листа
            <select value={listId} disabled={!canWrite} onChange={(e) => smeni(e.target.value)}>
              <option value="">— без листа —</option>
              {lists.filter((l) => l.is_active || String(l.id) === String(listId))
                .map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </label>
          <div className="field">
            Специални цени
            <div className="field-static">
              {rules == null ? '…' : `${aktivni} ${aktivni === 1 ? 'правило' : 'правила'}`}
            </div>
          </div>
          <Link className="button" to={`/price-lists?partnerId=${partner.id}`}>
            Отвори специалните цени
          </Link>
          {msg && <span className="muted">{msg}</span>}
        </div>
      </div>
    </div>
  );
}

function SupplierLinks({ partnerId, canWrite }) {
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState('');
  const [picking, setPicking] = useState(null);
  const [error, setError] = useState(null);

  const load = () => api.get(withQuery('/api/supplier-links', { partnerId, limit: 200 }))
    .then((r) => setItems(r.items))
    .catch((e) => setError(e.message));

  useEffect(() => { load(); }, [partnerId]);

  async function forget(link) {
    if (!window.confirm(`Забравяне на връзката „${link.supplier_code ?? link.description_key}"?`)) {
      return;
    }
    try { await api.del(`/api/supplier-links/${link.id}`); load(); }
    catch (e) { setError(e.message); }
  }

  async function relink(link, productId) {
    if (!productId) { setPicking(null); return; }
    try {
      await api.patch(`/api/supplier-links/${link.id}`, { productId });
      setPicking(null);
      load();
    } catch (e) { setError(e.message); }
  }

  if (!items) return null;

  const shown = query.trim()
    ? items.filter((i) => `${i.supplier_code ?? ''} ${i.description_key ?? ''} ${i.product_name}`
        .toLowerCase().includes(query.trim().toLowerCase()))
    : items;

  return (
    <div className="card">
      <div className="card-head">
        Запомнени връзки от фактури
        <div style={{ flex: 1 }} />
        <span className="muted" style={{ fontSize: 12 }}>{items.length}</span>
      </div>
      <div className="card-body" style={{ paddingBottom: 8 }}>
        <p className="hint" style={{ marginTop: 0 }}>
          По тях разчитането познава стоката без питане. Сгрешена връзка се
          поправя или забравя тук — иначе се връща при всяка следваща фактура.
        </p>
        {items.length > 8 && (
          <input type="search" autoComplete="off" placeholder="търсене по номер или стока"
            value={query} onChange={(e) => setQuery(e.target.value)}
            style={{ maxWidth: 320 }} />
        )}
        {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
      </div>

      {shown.length > 0 && (
        <div className="table-wrap">
          <table className="compact">
            <thead>
              <tr>
                <th>Каталожен номер</th>
                <th>Наша стока</th>
                <th className="num" style={{ width: 90 }}>Ползвана</th>
                {canWrite && <th style={{ width: 150 }} />}
              </tr>
            </thead>
            <tbody>
              {shown.map((l) => (
                <tr key={l.id}>
                  <td>
                    {l.supplier_code ?? <span className="muted">по описание</span>}
                    {l.description_key && !l.supplier_code && (
                      <div className="muted" style={{ fontSize: 11 }}>
                        {l.description_key.slice(0, 60)}
                      </div>
                    )}
                  </td>
                  <td>
                    {picking === l.id ? (
                      <ProductLookup value={null} label=""
                        placeholder="посочи вярната стока"
                        onPick={(id) => relink(l, id)} />
                    ) : (
                      <>
                        <Link to={`/products/${l.product_id}`}>{l.product_name}</Link>
                        <div className="muted" style={{ fontSize: 11 }}>{l.group_name}</div>
                      </>
                    )}
                  </td>
                  <td className="num muted">{l.times_used}</td>
                  {canWrite && (
                    <td>
                      <button className="link" onClick={() => setPicking(l.id)}>промени</button>
                      <button className="link" style={{ marginLeft: 10 }}
                        onClick={() => forget(l)}>забрави</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {shown.length === 0 && (
        <div className="card-body">
          <p className="empty" style={{ margin: 0 }}>
            {items.length === 0
              ? 'Още няма запомнени връзки от този доставчик.'
              : 'Няма съвпадение по търсенето.'}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Клиентите на агента.
 *
 * Връзката се пази при клиента, затова добавянето тук е същото, което е
 * и полето „Агент" в неговата карта — само гледано от другата страна.
 * Един клиент е на най-много един агент; ако вече е зает, сървърът казва
 * чий е, вместо да го отнеме мълчаливо.
 */
function AgentClients({ agentId, canWrite }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [since, setSince] = useState('');
  const [editingSinceId, setEditingSinceId] = useState(null);

  const load = () => api.get(`/api/partners/${agentId}/clients`)
    .then((r) => setItems(r.items))
    .catch((e) => setError(e.message));

  useEffect(() => { load(); }, [agentId]);

  function pick(clientId) {
    if (!clientId) { setAdding(false); return; }
    setPendingId(clientId);
    setSince(isoDate());
  }

  function cancelPending() {
    setPendingId(null);
    setAdding(false);
  }

  async function confirmAdd() {
    setError(null);
    try {
      await api.post(`/api/partners/${agentId}/clients`, { clientId: pendingId, since: since || null });
      cancelPending();
      load();
    } catch (e) { setError(e.message); }
  }

  /** Само датата се сменя — клиентът вече е на този агент. */
  async function changeSince(client, value) {
    setError(null);
    try {
      await api.post(`/api/partners/${agentId}/clients`, { clientId: client.id, since: value || null });
      setEditingSinceId(null);
      load();
    } catch (e) { setError(e.message); }
  }

  async function remove(client) {
    if (!window.confirm(`„${client.name}" да се махне от агента?`)) return;
    setError(null);
    try { await api.del(`/api/partners/${agentId}/clients/${client.id}`); load(); }
    catch (e) { setError(e.message); }
  }

  if (!items) return null;

  return (
    <div className="card">
      <div className="card-head">
        Клиенти на агента
        <div style={{ flex: 1 }} />
        <span className="muted" style={{ fontSize: 12 }}>{items.length}</span>
      </div>
      <div className="card-body" style={{ paddingBottom: 8 }}>
        <p className="hint" style={{ marginTop: 0 }}>
          Продажбите към тези клиенти носят комисионна на агента. Агентът се
          записва по документа сам, в деня на продажбата — датата отдолу
          важи само за по-старите продажби, които още нямат агент по себе
          си, и определя откъде нататък те влизат в комисионната му.
        </p>
        {canWrite && (
          pendingId ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="muted" style={{ fontSize: 12 }}>клиент от</span>
              <DateField value={since} onChange={setSince} />
              <button className="primary" onClick={confirmAdd}>Добави</button>
              <button onClick={cancelPending}>Отказ</button>
            </div>
          ) : adding ? (
            <Lookup url="/api/partners" params={{ type: 'client' }} value={null} label=""
              placeholder="търсене на клиент" onPick={pick} />
          ) : (
            <button onClick={() => setAdding(true)}>Добави клиент</button>
          )
        )}
        {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
      </div>

      {items.length > 0 && (
        <div className="table-wrap">
          <table className="compact">
            <thead>
              <tr>
                <th>Клиент</th><th>ЕИК</th><th>Град</th><th>Телефон</th><th>От дата</th>
                {canWrite && <th style={{ width: 90 }} />}
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/partners/${c.id}`}>{c.name}</Link>
                    {!Number(c.is_active) && <span className="muted"> · спрян</span>}
                  </td>
                  <td className="muted">{c.eik ?? ''}</td>
                  <td className="muted">{c.city ?? ''}</td>
                  <td className="muted">{c.phone ?? ''}</td>
                  <td className="muted">
                    {editingSinceId === c.id ? (
                      <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <DateField value={c.since ?? ''} onChange={(v) => changeSince(c, v)} />
                        <button className="link" onClick={() => setEditingSinceId(null)}>отказ</button>
                      </span>
                    ) : canWrite ? (
                      <button className="link" onClick={() => setEditingSinceId(c.id)}>
                        {c.since ? date(c.since) : 'от край време'}
                      </button>
                    ) : (c.since ? date(c.since) : 'от край време')}
                  </td>
                  {canWrite && (
                    <td><button className="link" onClick={() => remove(c)}>махни</button></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {items.length === 0 && (
        <div className="card-body">
          <p className="empty" style={{ margin: 0 }}>Този агент още няма клиенти.</p>
        </div>
      )}
    </div>
  );
}

/**
 * Процентите по групи.
 *
 * Общият процент стои горе, при самия агент. Тук се пише само това,
 * което се различава от него. Подгрупа без свой процент взима процента
 * на групата над нея — затова редът „наследено от" се показва изрично:
 * иначе човек би задавал по едно и също число на пет подгрупи.
 */
function AgentCommissions({ agentId, general, canWrite }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/api/partners/${agentId}/commissions`)
    .then((d) => {
      setData(d);
      setRows(d.items.map((i) => ({ groupId: i.groupId, path: i.path, percent: i.own })));
    })
    .catch((e) => setError(e.message));

  useEffect(() => { load(); }, [agentId]);

  if (!data) return null;

  const used = new Set(rows.map((r) => r.groupId));

  // Несъхраненото се вижда, за да не остане екранът да изглежда записан.
  const dirty = rows.length !== data.items.length
    || rows.some((r) => {
      const was = data.items.find((i) => i.groupId === r.groupId);
      return !was || Number(was.own) !== Number(r.percent);
    });

  function addGroup(groupId) {
    const g = data.groups.find((x) => x.groupId === Number(groupId));
    if (!g) return;
    setRows((rs) => [...rs, { groupId: g.groupId, path: g.path, percent: 0 }]);
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const fresh = await api.put(`/api/partners/${agentId}/commissions`, {
        items: rows.map((r) => ({ groupId: r.groupId, percent: Number(r.percent) || 0 })),
      });
      setData(fresh);
      setRows(fresh.items.map((i) => ({ groupId: i.groupId, path: i.path, percent: i.own })));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        Комисионна по групи
        <div style={{ flex: 1 }} />
        <span className="muted" style={{ fontSize: 12 }}>общо {general}%</span>
      </div>
      <div className="card-body" style={{ paddingBottom: 8 }}>
        <p className="hint" style={{ marginTop: 0 }}>
          Процент върху оборота без ДДС. Тук се пише само това, което се
          различава от общия процент — подгрупа без свой процент взима този
          на групата над нея. Нулата е стойност: „от тази група няма
          комисионна".
        </p>
        {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
      </div>

      {rows.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Група</th>
                <th className="num" style={{ width: 120 }}>Процент</th>
                {canWrite && <th style={{ width: 90 }} />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.groupId}>
                  <td>{r.path}</td>
                  <td className="num">
                    <input type="number" step="0.01" min="0" max="100" value={r.percent}
                      disabled={!canWrite}
                      style={{ width: 90, textAlign: 'right' }}
                      onChange={(e) => setRows((rs) =>
                        rs.map((x, j) => (j === i ? { ...x, percent: e.target.value } : x)))} />
                  </td>
                  {canWrite && (
                    <td>
                      <button className="link"
                        onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>махни</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card-body">
        {rows.length === 0 && (
          <p className="empty" style={{ marginTop: 0 }}>
            Няма отделни проценти — за всичко важат общите {general}%.
          </p>
        )}
        <div className="toolbar" style={{ marginBottom: 0 }}>
          {canWrite && (
            <GroupPicker exclude={used} onPick={addGroup} />
          )}
          {canWrite && (
            <button className="primary" onClick={save} disabled={busy || !dirty}>
              {busy ? 'Записва…' : 'Запис'}
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button className="link" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'скрий сметката по групи' : 'какво излиза за всяка група'}
          </button>
        </div>
      </div>

      {showAll && (
        <div className="table-wrap">
          <table className="compact">
            <thead>
              <tr>
                <th>Група</th>
                <th className="num" style={{ width: 100 }}>Стоки</th>
                <th className="num" style={{ width: 100 }}>Процент</th>
                <th>Откъде</th>
              </tr>
            </thead>
            <tbody>
              {data.groups.map((g) => (
                <tr key={g.groupId}>
                  <td>{g.path}</td>
                  <td className="num muted">{g.productCount}</td>
                  <td className="num">{g.effective}%</td>
                  <td className="muted">
                    {g.own !== null ? 'своя' : g.fromPath ? `от „${g.fromPath}"` : 'общият'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PartnerFigures({ partner, role }) {
  const isSupplier = role === 'supplier' || role === 'both';
  const isClient = role !== 'supplier';
  const t = partner.turnover ?? {};

  return (
    <>
      <div className="tiles" style={{ marginTop: 16 }}>
        {isClient && (
          <div className="tile">
            <div className="label">Дължи ни</div>
            <div className="value" style={{ color: Number(partner.due_eur) > 0.01 ? 'var(--danger)' : undefined }}>
              {money(partner.due_eur)}
            </div>
            <div className="sub">с ДДС · начално салдо {money(partner.opening_balance)}</div>
          </div>
        )}
        {isSupplier && (
          <div className="tile">
            <div className="label">Ние му дължим</div>
            <div className="value" style={{ color: Number(partner.owed_eur) > 0.01 ? 'var(--warn)' : undefined }}>
              {money(partner.owed_eur)}
            </div>
            <div className="sub">
              с ДДС · неплатени доставки
              {Number(partner.opening_balance) < -0.01
                && ` + ${money(-partner.opening_balance)} от старата програма`}
            </div>
          </div>
        )}
        {isSupplier && (
          <LastDeal label="Последна доставка" deal={t.last?.purchase} />
        )}
        {isClient && !isSupplier && (
          <LastDeal label="Последна продажба" deal={t.last?.sale} />
        )}
      </div>

      {isSupplier && <TurnoverTable title="Оборот от доставки" data={t.purchase} />}
      {isClient && <TurnoverTable title="Оборот от продажби" data={t.sale} />}
      {isSupplier && isClient && <LastDeal label="Последна продажба" deal={t.last?.sale} tile />}
    </>
  );
}

function LastDeal({ label, deal, tile }) {
  const body = (
    <div className="tile">
      <div className="label">{label}</div>
      <div className="value" style={{ fontSize: 18 }}>
        {deal?.doc_date ? date(deal.doc_date) : '—'}
      </div>
      <div className="sub">
        {deal
          ? <>{money(deal.net)} без ДДС · <strong>{money(deal.gross)}</strong> с ДДС</>
          : 'няма'}
      </div>
    </div>
  );
  return tile ? <div className="tiles" style={{ marginTop: 12 }}>{body}</div> : body;
}

function TurnoverTable({ title, data }) {
  const rows = [
    ['Този месец', 'month'],
    ['Тази година', 'year'],
    ['От началото', 'all'],
  ];
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head">{title}</div>
      <table className="compact">
        <thead>
          <tr>
            <th>Период</th>
            <th className="num">Документи</th>
            <th className="num">Без ДДС</th>
            <th className="num">С ДДС</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, key]) => (
            <tr key={key}>
              <td>{label}</td>
              <td className="num muted">{data?.documents?.[key] ?? 0}</td>
              <td className="num">{money(data?.net?.[key] ?? 0)}</td>
              <td className="num"><strong>{money(data?.gross?.[key] ?? 0)}</strong></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Field({ label, hint, wide, children }) {
  return (
    <label className="field" style={wide ? { gridColumn: 'span 2' } : undefined}>
      {label}
      {children}
      {hint && <span className="hint">{hint}</span>}
    </label>
  );
}

/**
 * В режим на преглед заключената кутийка се чете зле — не личи дали е
 * отметната. Затова там показваме текст, а кутийка има само при редакция.
 */
function Check({ label, checked, onChange, disabled }) {
  if (disabled) {
    return (
      <div className="field">
        {label}
        <div className="readonly-value">
          {checked ? <span className="badge ok">да</span> : <span className="badge muted">не</span>}
        </div>
      </div>
    );
  }
  return (
    <label style={{ display: 'flex', gap: 7, alignItems: 'center', alignSelf: 'end', paddingBottom: 8 }}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function toForm(p) {
  return {
    name: p.name ?? '',
    type: p.type ?? 'client',
    isPerson: !!Number(p.is_person),
    eik: p.eik ?? '',
    vatNumber: p.vat_number ?? '',
    isVatRegistered: !!Number(p.is_vat_registered),
    country: p.country ?? 'BG',
    city: p.city ?? '',
    address: p.address ?? '',
    postCode: p.post_code ?? '',
    shipCity: p.ship_city ?? '',
    shipAddress: p.ship_address ?? '',
    phone: p.phone ?? '',
    email: p.email ?? '',
    mol: p.mol ?? '',
    bankName: p.bank_name ?? '',
    bankBic: p.bank_bic ?? '',
    bankIban: p.bank_iban ?? '',
    defaultPriceLevel: p.default_price_level ?? 'retail',
    defaultPaymentMethod: p.default_payment_method ?? 'cash',
    discountPercent: Number(p.discount_percent ?? 0),
    creditLimit: Number(p.credit_limit ?? 0),
    creditDays: Number(p.credit_days ?? 0),
    blockOnOverdue: !!Number(p.block_on_overdue),
    openingBalance: Number(p.opening_balance ?? 0),
    agentId: p.agent_id ? Number(p.agent_id) : null,
    agentName: p.agent_name ?? null,
    agentSince: p.agent_since ?? null,
    commissionPercent: Number(p.commission_percent ?? 0),
    isActive: !!Number(p.is_active),
    note: p.note ?? '',
  };
}
